import {
  ASSETS_STORE,
  IndexedService,
  INSPECTIONS_STORE,
  ISSUE_MEDIAS_STORE,
  ISSUES_STORE,
  logger,
  NOTES_STORE,
  OUTLINES_STORE,
  SYSTEM2DS_STORE,
  TEXTS_STORE,
  WW_INSPECTIONS_COLLECTION,
} from '@damofire/data-commons';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { Inspection } from '../../inspection/inspection.dto';
import { WarehouseSyncStepTask } from '../executors';

/** The stores a warehouse-state snapshot is built from. */
const SNAPSHOT_STORES = [ASSETS_STORE, INSPECTIONS_STORE, ISSUES_STORE, NOTES_STORE, ISSUE_MEDIAS_STORE, SYSTEM2DS_STORE, OUTLINES_STORE, TEXTS_STORE];

/**
 * Last push task: tells the server which inspections completed on this device are now fully pushed,
 * issues, notes and pictures included, by clearing their awaitingSync flag. The server builds an
 * inspection's snapshot only once that flag is cleared (see IdbInspectionService.update).
 *
 * A warehouse that still has changes waiting to be pushed is left for the next sync.
 */
export class WarehousePushSyncCompletedSyncStepTask implements WarehouseSyncStepTask {
  // Built by the step factory, which hands every task the environment first.
  constructor(
    _environment: unknown,
    private readonly idbService: IndexedService,
  ) {}

  name(): string {
    return `PUSH_DONE_TASK`;
  }

  async execute(): Promise<void> {
    const awaiting = (await this.idbService.getAll<Inspection>(INSPECTIONS_STORE)).filter((inspection: Inspection) => inspection.awaitingSync);
    const pendingByDivision = new Map<string, string | null>();

    for (const inspection of awaiting) {
      const divisionId = inspection.division._id;
      if (!pendingByDivision.has(divisionId)) {
        pendingByDivision.set(divisionId, await this.findPendingStore(divisionId));
      }

      const pendingStore = pendingByDivision.get(divisionId);
      if (pendingStore) {
        logger.info('Inspection still has changes to push, keeping it awaiting sync', { category: 'sync', id: inspection._id, division: divisionId, pendingStore });
        continue;
      }

      try {
        await this.clearRemoteFlag(inspection._id);
        await this.idbService.update(INSPECTIONS_STORE, { ...inspection, awaitingSync: false });
        logger.info('Inspection fully pushed', { category: 'sync', id: inspection._id, division: divisionId });
      } catch (error) {
        // Left flagged, so the next sync tries again.
        logger.error('Could not report inspection as fully pushed', { category: 'sync', id: inspection._id, division: divisionId, error });
      }
    }
  }

  protected async clearRemoteFlag(inspectionId: string): Promise<void> {
    await updateDoc(doc(getFirestore(), `${WW_INSPECTIONS_COLLECTION}/${inspectionId}`), { awaitingSync: false });
  }

  /** The first operations store still holding changes for the division, or null once everything is pushed. */
  private async findPendingStore(divisionId: string): Promise<string | null> {
    for (const store of SNAPSHOT_STORES) {
      if ((await this.idbService.getAllFromIndex(`${store}-operations`, 'division', divisionId)).length) {
        return `${store}-operations`;
      }
    }

    return null;
  }
}
